import { useCallback, useEffect, useState, useMemo } from 'react'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import type { BetType } from 'src/types/domain/BetDetails'
import BullseyeIcon from 'src/app/assets/icons/ico-bullseye.svg'
import RandomIcon from 'src/app/assets/icons/ico-random.svg'
import RedoIcon from 'src/app/assets/icons/ico-redo-alt.svg'
import CogIcon from 'src/app/assets/icons/ico-cog.svg'
import FlagCheckeredIcon from 'src/app/assets/icons/ico-flag-checkered.svg'
import TrashIcon from 'src/app/assets/icons/ico-trash.svg'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Marble } from 'src/types/domain/Marble'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage, useGetGameBetConfig } from 'src/store/game'
import { useGetCurrentPlayer } from 'src/store/player'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { useGetGameChannel, useGetJoinedGameChannelId } from 'src/store/socket'
import { useProbabilityMultipliers } from 'src/hooks/useProbabilityMultipliers'
import { getSelectionTypeOptions } from 'src/utils/gameTypeUtils'
import { isExoticBetEnabled } from 'src/utils/isExoticBetEnabled'
import { BoosterItem, BonusItem, isBooster, isBonus, isClaimed } from 'src/features/inventory'
import { useGetAutoBooster, useInventoryStore } from 'src/features/inventory/store'
import { useGetCurrentStreak } from 'src/store/streak'
import { useGetPlayerLevel } from 'src/store/playerLevel'
import { FloatingButtonCircles } from 'src/components/FloatingButtonCircles'
import { displayAmountClean } from 'src/utils/displayAmountClean'
import { wrapInterpolatedValues } from '../utils/wrapInterpolatedValues'
import { AutoBetSwitch } from './AutoBetSwitch'
import { NextRaceBetHeader } from './NextRaceBetHeader'
import { MarblePaneMarble } from './MarblePaneMarble'
import { MarblePaneMarbleWrapper } from './MarblePaneMarbleWrapper'
import type { Race } from 'src/types/domain/Race'

interface AutoBet {
  isEnabled: boolean
  isActive: boolean
  toggle: () => void
}

type Props = Readonly<{
  betType: BetType
  mode: 'pickWinner' | 'pickOrder'
  game: MarbleGame
  currentRace: Race
  isRaceReady: boolean
  selectedMarbles: Marble[]
  handleButtonClick: (callback: () => void) => void
  handleResetSelection: () => void
  handleRepeatLastBet: () => void
  showRepeatButton: boolean
  setBetType: (betType: BetType) => void
  handlePositionSelection: (marble: Marble, position: number) => void
  selectedPositions: Record<string, number>
  toggleMarbleSelection: (marble: Marble) => void
  setSelectedMarbles: React.Dispatch<React.SetStateAction<Marble[]>>
  setMode: React.Dispatch<React.SetStateAction<'pickWinner' | 'pickOrder'>>
  initialIsExactOrder?: boolean
  showAdvancedOptions: boolean
  onAdvancedOptionsChange: (show: boolean) => void
  autoBet: AutoBet
}>

export default function ChooseMarblePane({
  mode,
  setMode,
  betType,
  game,
  currentRace,
  isRaceReady,
  selectedMarbles,
  handleButtonClick,
  handleResetSelection,
  handleRepeatLastBet,
  showRepeatButton,
  setBetType,
  handlePositionSelection,
  selectedPositions,
  toggleMarbleSelection,
  setSelectedMarbles,
  initialIsExactOrder = false,
  autoBet,
  showAdvancedOptions,
  onAdvancedOptionsChange
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const [isExactOrderSelected, setIsExactOrderSelected] = useState(initialIsExactOrder)
  const [selectionOrder, setSelectionOrder] = useState<string[]>([])
  const [activeMarbles, setActiveMarbles] = useState<Marble[]>(
    game.marbles.filter((marble) => marble.active)
  )
  const [multipliers, setMultipliers] = useState<Record<string, number>>()

  const player = useGetCurrentPlayer()
  const gameChannel = useGetGameChannel()
  const joinedGameChannelGameId = useGetJoinedGameChannelId()
  const betConfig = useGetGameBetConfig(player)
  const getProbabilityMultipliers = useProbabilityMultipliers({ gameChannel })
  const { playEffect } = useSoundEffect()

  // Get the oldest auto-apply booster for the current game
  const oldestBooster = useGetAutoBooster(game.id)

  // Get current streak boost
  const currentStreak = useGetCurrentStreak()

  // Get current player level boost
  const playerLevel = useGetPlayerLevel()

  // Get manually claimed items for the current game
  const { activatedItemIds, items } = useInventoryStore((state) => ({
    activatedItemIds: state.activatedItemIds,
    items: state.items
  }))

  const activatedBoosters = useMemo(
    () =>
      items
        .filter(
          (item) =>
            activatedItemIds.includes(item.id) &&
            isBooster(item) &&
            isClaimed(item) &&
            (item.applicableGames.length === 0 || item.applicableGames.includes(game.id))
        )
        .filter(isBooster),
    [items, activatedItemIds, game.id]
  )

  const activatedBonuses = useMemo(
    () =>
      items
        .filter(
          (item) =>
            activatedItemIds.includes(item.id) &&
            isBonus(item) &&
            isClaimed(item) &&
            (item.applicableGames.length === 0 || item.applicableGames.includes(game.id))
        )
        .filter(isBonus),
    [items, activatedItemIds, game.id]
  )

  // Get inventory item count for mobile display

  // Calculate total boost percentage from all active boosters + streak boost + level boost
  const totalBoostPercentage = useMemo(() => {
    let total = 0

    // Add auto booster percentage
    if (oldestBooster && oldestBooster.boost.unit === 'percentage') {
      total += oldestBooster.boost.value
    }

    // Add manually activated boosters
    activatedBoosters.forEach((booster) => {
      if (booster.boost.unit === 'percentage') {
        total += booster.boost.value
      }
    })

    // Add streak boost percentage
    if (currentStreak?.streakBoostPercent) {
      total += currentStreak.streakBoostPercent
    }

    // Add player level boost (multiplierBonus is already in percentage points, e.g., 0.3 = 0.3%)
    if (playerLevel?.multiplierBonus) {
      total += playerLevel.multiplierBonus
    }

    return total > 0 ? total : undefined
  }, [oldestBooster, activatedBoosters, currentStreak, playerLevel])

  useEffect(() => {
    if (mode === 'pickOrder' && isExactOrderSelected) {
      // Reset selection order to match current marble selection
      setSelectionOrder(selectedMarbles.map((m) => m.uuid))
    }
  }, [mode, isExactOrderSelected, selectedMarbles])

  useEffect(() => {
    const marbles = game.marbles.filter((marble) => marble.active)
    setActiveMarbles(marbles)

    const updateMultipliers = async () => {
      const probMultipliers = await getProbabilityMultipliers(marbles, game.id)
      setMultipliers(probMultipliers?.multipliers)
    }

    if (gameChannel?.state === 'joined' && joinedGameChannelGameId === game.id) {
      void updateMultipliers()
    }
  }, [game.id, game.marbles, gameChannel, getProbabilityMultipliers, joinedGameChannelGameId])

  const sortMarblesByMultipliers = (a: Marble, b: Marble) => {
    // For coin_flip, fishtank, irl, and irl_alt, always sort by marble number
    if (
      game.track.gameType === 'coin_flip' ||
      game.track.gameType === 'fishtank' ||
      game.track.gameType === 'irl' ||
      game.track.gameType === 'irl_alt' ||
      game.track.gameType === 'duck_river' ||
      game.track.gameType === 'snow_run' ||
      game.track.gameType === 'social_betting' ||
      game.track.gameType === 'crash'
    ) {
      return Number(a.number) - Number(b.number)
    }
    const probA = betConfig?.probabilities[a.uuid] ?? 0
    const probB = betConfig?.probabilities[b.uuid] ?? 0
    if (probA === probB) {
      return Number(a.number) - Number(b.number)
    }
    return probB - probA
  }

  // Use equal odds betting ui for fixed odds games on Ducks, Marbles, and Fast Lane
  const showSimpleOdds =
    game.track.gameType === 'marbles' ||
    game.track.gameType === 'ducks' ||
    game.track.gameType === 'fast_lane'

  const showExactOrderSelection =
    mode === 'pickOrder' && isExactOrderSelected && selectedMarbles.length >= 1 // should just be 1 or greater

  useEffect(() => {
    if (mode === 'pickOrder') {
      if (selectedMarbles.length === 2) {
        setBetType(isExactOrderSelected ? 'StraightForecast' : 'ReverseForecast')
      } else if (selectedMarbles.length === 3) {
        setBetType(isExactOrderSelected ? 'Tricast' : 'CombinationTricast')
      } else {
        setBetType('PickWinner')
      }
    } else {
      setBetType('PickWinner')
      setIsExactOrderSelected(false)
    }
  }, [mode, selectedMarbles.length, isExactOrderSelected])

  const handleModeChange = (newMode: 'pickWinner' | 'pickOrder') => {
    if (newMode === 'pickOrder' && selectedMarbles.length > 3) {
      setSelectedMarbles([])
      setSelectionOrder([])
      playEffect('cancel')
    } else if (newMode === 'pickWinner' && selectedMarbles.length > game.maxPickableMarbles) {
      // Clear selections if switching to pickWinner mode with too many marbles
      setSelectedMarbles([])
      setSelectionOrder([])
      playEffect('cancel')
    }

    setMode(newMode)
    if (newMode === 'pickWinner') {
      setIsExactOrderSelected(false)
      setSelectionOrder([])
    }

    playEffect('click')
  }

  const handleMarbleSelection = (marble: Marble) => {
    const isSelected = selectedMarbles.some((m) => m.uuid === marble.uuid)

    const deselectMarble = () => {
      setSelectionOrder((prev) => prev.filter((uuid) => uuid !== marble.uuid))
      toggleMarbleSelection(marble)
    }

    const selectMarble = () => {
      setSelectionOrder((prev) => [...prev, marble.uuid])
      toggleMarbleSelection(marble)
    }

    if (mode === 'pickWinner') {
      if (game.maxPickableMarbles === 1) {
        setSelectedMarbles([]) // Reset all
        setSelectionOrder([marble.uuid])
        toggleMarbleSelection(marble)
        return
      }

      if (isSelected) {
        deselectMarble()
      } else if (selectedMarbles.length < game.maxPickableMarbles) {
        selectMarble()
      }

      return
    }

    if (mode === 'pickOrder') {
      if (!isSelected && selectedMarbles.length >= 3) return

      isSelected ? deselectMarble() : selectMarble()
    }
  }

  const handleAdvancedClick = () => {
    handleButtonClick(() => {
      if (showAdvancedOptions && mode === 'pickOrder') {
        handleModeChange('pickWinner')
      }
      onAdvancedOptionsChange(!showAdvancedOptions)
      playEffect('click')
    })
  }

  useEffect(() => {
    if (isExactOrderSelected && mode === 'pickOrder') {
      // Create new positions object based on selection order
      const newPositions: Record<string, number> = {}
      selectionOrder.forEach((uuid, index) => {
        if (selectedMarbles.some((m) => m.uuid === uuid)) {
          newPositions[uuid] = index + 1
        }
      })

      // Update positions using existing handler
      Object.entries(newPositions).forEach(([uuid, position]) => {
        const marble = selectedMarbles.find((m) => m.uuid === uuid)
        if (marble) {
          handlePositionSelection(marble, position)
        }
      })
    }
  }, [isExactOrderSelected, selectionOrder, selectedMarbles, mode])

  const handleForecastOrderClick = (order: 'anyOrder' | 'exactOrder') => {
    if (order === 'exactOrder') {
      if (betType === 'ReverseForecast') {
        setBetType('StraightForecast')
      } else if (betType === 'CombinationTricast') {
        setBetType('Tricast')
      }
      setIsExactOrderSelected(true)
    } else {
      if (betType === 'StraightForecast') {
        setBetType('ReverseForecast')
      } else if (betType === 'Tricast') {
        setBetType('CombinationTricast')
      }
      setIsExactOrderSelected(false)
    }
  }

  const handleReset = () => {
    handleButtonClick(() => {
      handleResetSelection()
      setSelectionOrder([])
    })
  }

  const handleRepeat = () => {
    handleButtonClick(() => {
      handleRepeatLastBet()
    })
  }

  const generateInfoText = useCallback(() => {
    const selectionOptions = getSelectionTypeOptions(game.track.gameType, t)

    // Handle case where no marbles are selected yet
    if (selectedMarbles.length === 0) {
      if (mode === 'pickOrder') {
        return wrapInterpolatedValues(
          t('Select up to 3 {{selectionType}} in {{orderType}}', {
            selectionType: selectionOptions.plural,
            orderType: isExactOrderSelected ? t('exact order') : t('any order')
          })
        )
      }
      // Pick Winner mode with no selections
      const isFastLaneEmpty = game.track.gameType === 'fast_lane'
      return wrapInterpolatedValues(
        isFastLaneEmpty
          ? t('Pick up to {{maxPickableMarbles}} {{selectionType}} to be last on the treadmill', {
              maxPickableMarbles: game.maxPickableMarbles,
              selectionType: selectionOptions.plural
            })
          : t('Select up to {{maxPickableMarbles}} {{selectionType}} to win race', {
              maxPickableMarbles: game.maxPickableMarbles,
              selectionType:
                game.maxPickableMarbles === 1 ? selectionOptions.singular : selectionOptions.plural
            })
      )
    }

    // Pick Order mode with insufficient selections
    if (mode === 'pickOrder' && selectedMarbles.length < 2) {
      return t('Pick at least 2 {{selectionType}}', {
        selectionType: selectionOptions.plural
      })
    }

    // Pick Winner mode with selections
    if (betType === 'PickWinner') {
      const isFastLane = game.track.gameType === 'fast_lane'
      if (selectedMarbles.length === 1) {
        return wrapInterpolatedValues(
          isFastLane
            ? t('Win if your {{count}} {{selectionType}} is last on the treadmill', {
                count: 1,
                selectionType: selectionOptions.singular
              })
            : t('Win if your {{count}} {{selectionType}} finishes first', {
                count: 1,
                selectionType: selectionOptions.singular
              })
        )
      }
      return wrapInterpolatedValues(
        isFastLane
          ? t('Win if any of your {{count}} {{selectionType}} is last on the treadmill', {
              count: selectedMarbles.length,
              selectionType: selectionOptions.plural
            })
          : t('Win if any of your {{count}} {{selectionType}} finish first', {
              count: selectedMarbles.length,
              selectionType: selectionOptions.plural
            })
      )
    }

    // Handle Pick Order variations
    const isForecast = ['ReverseForecast', 'StraightForecast'].includes(betType)
    const count = isForecast ? '2' : '3'

    return wrapInterpolatedValues(
      t('Pick the top {{count}} finishers in {{orderType}} to win', {
        count,
        orderType: isExactOrderSelected ? t('exact order') : t('any order')
      })
    )
  }, [
    betType,
    selectedMarbles.length,
    mode,
    isExactOrderSelected,
    t,
    game.maxPickableMarbles,
    game.track.gameType
  ])

  if (!betConfig) return null

  return (
    <>
      {(oldestBooster || activatedBoosters.length > 0 || activatedBonuses.length > 0) && (
        <div className="floating-inventory-container z-20">
          <div className="flex flex-col gap-2">
            {/* Auto-applied booster */}
            {oldestBooster && (
              <BoosterItem
                reward={`+${
                  oldestBooster.boost.unit === 'percentage'
                    ? `${oldestBooster.boost.value}%`
                    : oldestBooster.boost.value
                }`}
                tooltip={<span className="font-medium">{t('Boost is auto-applied')}</span>}
              />
            )}

            {/* Manually claimed boosters */}
            {activatedBoosters.map((booster) => (
              <BoosterItem
                key={booster.id}
                reward={`+${
                  booster.boost.unit === 'percentage'
                    ? `${booster.boost.value}%`
                    : booster.boost.value
                }`}
                tooltip={<span className="font-medium">{t('Manual boost is active')}</span>}
              />
            ))}

            {/* Auto-applied free bets (consumed automatically — no manual remove) */}
            {activatedBonuses.map((bonus) => (
              <BonusItem
                key={bonus.id}
                reward={`${getCurrencySymbol(bonus.currency)}${displayAmountClean(bonus.amount)}`}
                tooltip={<span className="font-medium">{t('Applied automatically')}</span>}
              />
            ))}
          </div>
        </div>
      )}

      <div className="floating-button-container">
        <div
          className={`button-collection ${
            showRepeatButton || selectedMarbles.length > 0 ? 'visible' : ''
          }`}
        >
          <button
            className={`floating-action-btn btn-repeat ${showRepeatButton && !selectedMarbles.length ? 'visible' : ''}`}
            onClick={handleRepeat}
            type="button"
          >
            <span className="icon">
              <RedoIcon />
            </span>
            <span className="label">{t('Repeat Bet')}</span>
            <FloatingButtonCircles variant="default" />
          </button>
          <button
            className={`floating-action-btn btn-reset ${selectedMarbles.length > 0 ? 'visible' : ''}`}
            onClick={handleReset}
            type="button"
          >
            <span className="icon">
              <TrashIcon />
            </span>
            <span className="label">{t('Clear')}</span>
            <FloatingButtonCircles variant="default" />
          </button>
        </div>
        {isExoticBetEnabled(game.maxPickableMarbles) && game.track.gameType !== 'fast_lane' && (
          <button
            className={`floating-action-btn advanced-button ${showAdvancedOptions ? 'active' : ''}`}
            onClick={handleAdvancedClick}
            type="button"
          >
            <span className="icon">
              <CogIcon />
            </span>
            <span className="label">{t('Options')}</span>
            <FloatingButtonCircles variant={showAdvancedOptions ? 'yellow' : 'default'} />
          </button>
        )}
        <AutoBetSwitch
          isActive={autoBet.isActive}
          isEnabled={autoBet.isEnabled}
          onToggle={autoBet.toggle}
        />
      </div>

      <div
        className={`choose-marbles choose-marbles--selector ${
          showExactOrderSelection
            ? 'choose-marbles--selector-exact-order'
            : 'choose-marbles--selector-any-order'
        }`}
      >
        <NextRaceBetHeader isRaceReady={isRaceReady} gameType={game.track.gameType} />

        {game.track.gameType !== 'coin_flip' &&
          game.track.gameType !== 'fishtank' &&
          game.track.gameType !== 'irl' &&
          game.track.gameType !== 'irl_alt' &&
          game.track.gameType !== 'duck_river' &&
          game.track.gameType !== 'snow_run' &&
          game.track.gameType !== 'social_betting' &&
          game.track.gameType !== 'crash' && (
            <div
              className={`info ${mode === 'pickOrder' ? 'info-pick-order' : 'info-pick-winner'}`}
            >
              {generateInfoText()}
            </div>
          )}

        <MarblePaneMarbleWrapper
          equalOdds={betConfig.equalOdds}
          marblesCount={activeMarbles.length}
          showSimpleOdds={showSimpleOdds}
          gameType={game.track.gameType}
        >
          {[...activeMarbles].sort(sortMarblesByMultipliers).map((marble) => {
            // Only show lightning indicators when betting for current race (race_ready/race_starting)
            // After race_started, we're betting on the next race which may not be lightning
            const isLightningBoosted =
              isRaceReady &&
              currentRace.isLightningRound &&
              currentRace.lightningBoostedItems?.includes(marble.uuid)

            return (
              <MarblePaneMarble
                boostPercentage={totalBoostPercentage}
                gameType={game.track.gameType}
                handleButtonClick={handleButtonClick}
                handleMarbleSelection={handleMarbleSelection}
                isExactOrderSelected={isExactOrderSelected}
                isLightningBoosted={isLightningBoosted}
                lightningBoostType={isRaceReady ? currentRace.lightningBoostType : null}
                lightningBoostPercent={isRaceReady ? currentRace.lightningBoostPercent : null}
                key={marble.uuid}
                marble={marble}
                multipliers={multipliers}
                probabilities={betConfig.probabilities}
                selectedMarbles={selectedMarbles}
                selectedPositions={selectedPositions}
              />
            )
          })}
        </MarblePaneMarbleWrapper>
      </div>

      <div
        className={`place-bet__nav ${showAdvancedOptions && game.track.gameType !== 'fast_lane' ? 'show' : 'hidden'}`}
      >
        <button
          className={`button-pick-winner ${mode === 'pickWinner' ? 'active' : ''}`}
          onClick={() => {
            handleButtonClick(() => {
              handleModeChange('pickWinner')
            })
          }}
          type="button"
        >
          <span>{t('Winner')}</span>
          <FlagCheckeredIcon />
        </button>

        <button
          className={`button-any-order ${
            mode === 'pickOrder' && !isExactOrderSelected ? 'active' : ''
          }`}
          onClick={() => {
            handleButtonClick(() => {
              handleModeChange('pickOrder')
              handleForecastOrderClick('anyOrder')
            })
          }}
          type="button"
        >
          <span>{t('Any order')}</span>
          <RandomIcon />
        </button>

        <button
          className={`button-exact-order ${
            mode === 'pickOrder' && isExactOrderSelected ? 'active' : ''
          }`}
          onClick={() => {
            handleButtonClick(() => {
              handleModeChange('pickOrder')
              handleForecastOrderClick('exactOrder')
            })
          }}
          type="button"
        >
          <span>{t('Exact order')}</span>
          <BullseyeIcon />
        </button>
      </div>
    </>
  )
}
